import { Instagram, Linkedin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { languagePagesFor, type LanguagePageSlug } from "@/lib/languagePages";

// One Teach/Learn link per language page. Each label is its own key ("Teach
// English" isn't "Teach" + a name in every language), and the Record makes a
// new page in lib/languagePages fail to compile until it has a teach label (a
// learn label is only needed when the page has a learn side).
const TEACH_LABEL_KEYS: Record<LanguagePageSlug, string> = {
  english: "footer.links.teachEnglish",
  french: "footer.links.teachFrench",
  spanish: "footer.links.teachSpanish",
  italian: "footer.links.teachItalian",
  japanese: "footer.links.teachJapanese",
  portuguese: "footer.links.teachPortuguese",
};
const LEARN_LABEL_KEYS: Partial<Record<LanguagePageSlug, string>> = {
  english: "footer.links.learnEnglish",
  french: "footer.links.learnFrench",
  spanish: "footer.links.learnSpanish",
  italian: "footer.links.learnItalian",
  japanese: "footer.links.learnJapanese",
};

// labelKey is resolved at render with t() (module-level array, no hook scope).
// The /apps/* entries are brand/product names (kept identical across locales).
const footerLinks = {
  company: [
    { labelKey: "footer.links.about", href: "/about" },
    { labelKey: "footer.links.workWithUs", href: "/careers" },
    { labelKey: "footer.links.blog", href: "/blog" },
    { labelKey: "footer.links.ourAuthors", href: "/authors" },
  ],
  support: [
    { labelKey: "footer.links.contactUs", href: "/contact" },
    { labelKey: "footer.links.needHelp", href: "/help" },
    { labelKey: "footer.links.faq", href: "/faq" },
    { labelKey: "footer.links.communityGuidelines", href: "/community-guidelines" },
  ],
  legal: [
    { labelKey: "footer.links.terms", href: "/terms" },
    { labelKey: "footer.links.privacy", href: "/privacy" },
    { labelKey: "footer.links.cookiePolicy", href: "/cookies" },
    // #1096: how tutors are listed, ordered and removed, reachable from every public page.
    { labelKey: "howWeOrderTutors.linkLabel", href: "/how-we-order-tutors" },
    // #683: the mentions légales French law requires (LCEN Art. 6-III).
    { labelKey: "footer.links.legalNotice", href: "/legal-notice" },
    // #1086: ending a subscription must be a clearly labelled function, always
    // reachable (French law, in force since June 2023). It opens the cancel flow,
    // after sign-in for a visitor who is not signed in (ProtectedRoute carries
    // the address through).
    { labelKey: "footer.links.cancelSubscription", href: "/student/buy-credits?cancel=1" },
  ],
  apps: [
    { labelKey: "footer.links.appJynio", href: "/apps/jynio" },
    { labelKey: "footer.links.appFlashcards", href: "/apps/flashcards" },
    { labelKey: "footer.links.appPuzzle", href: "/apps/puzzle" },
    { labelKey: "footer.links.appChat", href: "/apps/chat" },
  ],
  teach: languagePagesFor("teach").map(({ slug }) => ({ labelKey: TEACH_LABEL_KEYS[slug], href: `/become-a-tutor/${slug}` })),
  learn: languagePagesFor("learn").map(({ slug }) => ({ labelKey: LEARN_LABEL_KEYS[slug] as string, href: `/find-a-tutor/${slug}` })),
};

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:gap-8 lg:grid-cols-8">
          {/* Brand & Newsletter */}
          <div className="col-span-2 md:col-span-1 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4 min-h-11">
              <img src="/images/logo/navbar-logo.svg" alt="Jynio" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-display font-bold text-foreground">
                Jynio
              </span>
            </Link>

            <p className="text-muted-foreground text-sm max-w-sm">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Become a tutor */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.becomeTutor")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.teach.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Find a tutor */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.findTutor")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.learn.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Apps */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.ourApps")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.apps.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.company")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.legal")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
              {/* Lets users re-open the cookie preferences to withdraw or
                  change consent at any time (GDPR / ePrivacy requirement). */}
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("open-cookie-prefs"))}
                  className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-muted-foreground hover:text-foreground transition-colors text-left rounded-sm focus-ring"
                >
                  {t("footer.cookieSettings")}
                </button>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <p className="font-semibold text-foreground mb-4">{t("footer.support")}</p>
            <ul className="space-y-1 md:space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="inline-flex items-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-4 h-4" />
              <a href="mailto:hello@jynio.co" className="inline-flex items-center min-h-11 md:min-h-0 hover:text-foreground transition-colors">
                hello@jynio.co
              </a>
            </div>
            <LanguageSwitcher className="h-11 md:h-9 w-[180px]" />
          </div>

          {/* Social links */}
          <div className="flex items-center gap-4">
            <a
              href="https://x.com/JynioCo"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/jynio.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.linkedin.com/company/jynio/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>

          {/* Server-rendered in UTC: around New Year a visitor ahead of UTC reads a
              different year than the server wrote, which alone would make React
              discard the page. The browser's year is right; let it win quietly. */}
          <p className="text-sm text-muted-foreground" suppressHydrationWarning>
            {t("footer.rights", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
